// Ingreso con la autenticación EXISTENTE (POST /auth/sesion, cookie httpOnly). El mensaje de error
// es el del backend (clave incorrecta, usuario suspendido, demasiados intentos): no se inventa otro.
import { useState, type FormEvent } from 'react';
import { ErrorApi, api } from '../api/cliente';
import { presentarError } from '../errores/catalogo';
import { useSalud } from '../shell/salud';
import { useSesion } from './SesionContexto';

/** Igual que el servidor (packages/auth, LARGO_MINIMO_CLAVE): antes decía 8 y el servidor pedía 10. */
export const LARGO_MINIMO_CLAVE = 10;

export function FormularioIngreso({
  correoInicial = '',
  titulo,
}: {
  correoInicial?: string;
  titulo?: string;
}) {
  const { iniciar } = useSesion();
  const [correo, setCorreo] = useState(correoInicial);
  const [clave, setClave] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    if (enviando) return;
    setEnviando(true);
    setError(null);
    try {
      await iniciar(correo.trim(), clave);
    } catch (err) {
      setError(
        err instanceof ErrorApi && err.status === 401
          ? err.cuerpo.mensaje
          : presentarError(err).mensaje,
      );
      setClave('');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <form className="ingreso" onSubmit={enviar} aria-label="Ingreso">
      {titulo && <p className="ayuda">{titulo}</p>}
      <label className="campo">
        Correo
        <input
          type="email"
          autoComplete="username"
          autoFocus={!correoInicial}
          value={correo}
          onChange={(e) => setCorreo(e.target.value)}
          required
        />
      </label>
      <label className="campo">
        Clave
        <input
          type="password"
          autoComplete="current-password"
          autoFocus={!!correoInicial}
          value={clave}
          onChange={(e) => setClave(e.target.value)}
          required
        />
      </label>
      {error && (
        <div className="panel-error sev-autorizacion" role="alert">
          {error}
        </div>
      )}
      <button type="submit" className="primario" disabled={enviando}>
        {enviando ? 'Verificando…' : 'Ingresar'}
      </button>
    </form>
  );
}

/** El token de una invitación en la dirección (#/activar?token=…), si viene. */
function tokenDeInvitacion(): string | null {
  const m = /^#\/activar\?token=([^&]+)/.exec(window.location.hash);
  return m ? decodeURIComponent(m[1]!) : null;
}

/**
 * Activar la cuenta desde el enlace de invitación (Configuración › Usuarios): la persona define SU
 * clave; nadie más la conoce. Después entra con su correo y esa clave.
 */
function FormularioActivar({ token, alListo }: { token: string; alListo: () => void }) {
  const [clave, setClave] = useState('');
  const [repetir, setRepetir] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const valida = clave.length >= LARGO_MINIMO_CLAVE && clave === repetir;
  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    if (!valida || enviando) return;
    setEnviando(true);
    setError(null);
    try {
      await api.post('/auth/invitaciones/aceptar', { token, clave });
      alListo();
    } catch (err) {
      setError(presentarError(err).mensaje);
    } finally {
      setEnviando(false);
    }
  };
  return (
    <form className="ingreso" onSubmit={enviar} aria-label="Activar cuenta">
      <p className="ayuda">
        Bienvenido(a). Define tu clave para entrar al ERP (mínimo {LARGO_MINIMO_CLAVE} caracteres).
      </p>
      <label className="campo">
        Clave nueva
        <input
          type="password"
          autoComplete="new-password"
          autoFocus
          value={clave}
          onChange={(e) => setClave(e.target.value)}
          required
        />
      </label>
      <label className="campo">
        Repite la clave
        <input
          type="password"
          autoComplete="new-password"
          value={repetir}
          onChange={(e) => setRepetir(e.target.value)}
          required
        />
      </label>
      {clave.length > 0 && clave.length < LARGO_MINIMO_CLAVE && (
        <p className="ayuda">Faltan {LARGO_MINIMO_CLAVE - clave.length} caracteres.</p>
      )}
      {repetir.length > 0 && clave !== repetir && <p className="ayuda">Las claves no coinciden.</p>}
      {error && (
        <div className="panel-error sev-autorizacion" role="alert">
          {error}
        </div>
      )}
      <button type="submit" className="primario" disabled={!valida || enviando}>
        {enviando ? 'Activando…' : 'Activar mi cuenta'}
      </button>
    </form>
  );
}

export function PantallaIngreso() {
  const salud = useSalud();
  const [token, setToken] = useState(tokenDeInvitacion);
  const [activada, setActivada] = useState(false);
  return (
    <main className="pantalla-ingreso">
      <div className="caja-ingreso">
        <h1>
          MADECI <span>ERP</span>
        </h1>
        {/* Solo fuera de producción: en erp.madeci.cl decía «desarrollo / UAT» (29-10). */}
        {salud.data && salud.data.ambiente !== 'produccion' && (
          <p className="apagado">Ambiente de desarrollo / UAT — sin datos productivos</p>
        )}
        {token ? (
          <FormularioActivar
            token={token}
            alListo={() => {
              window.history.replaceState(null, '', window.location.pathname);
              setToken(null);
              setActivada(true);
            }}
          />
        ) : (
          <>
            {activada && (
              <p className="aviso-linea tono-ok" role="status">
                Cuenta activada. Entra con tu correo y la clave que definiste.
              </p>
            )}
            <FormularioIngreso />
          </>
        )}
      </div>
    </main>
  );
}

/** Sesión vencida en plena operación: se pide la clave SIN perder las pestañas abiertas. */
export function ReingresoSesion() {
  const { usuario, cerrar } = useSesion();
  return (
    <div className="modal-fondo">
      <div
        className="modal modal-angosto"
        role="dialog"
        aria-modal="true"
        aria-label="Sesión expirada"
      >
        <header className="modal-cabecera">
          <h2>Su sesión expiró</h2>
        </header>
        <div className="modal-cuerpo">
          <FormularioIngreso
            correoInicial={usuario?.correo ?? ''}
            titulo="Ingrese su clave para continuar. Lo que tenía abierto se mantiene."
          />
          <button type="button" className="enlace" onClick={() => void cerrar()}>
            Salir y descartar lo abierto
          </button>
        </div>
      </div>
    </div>
  );
}

export function SelectorEmpresa() {
  const { empresas, elegirEmpresa, usuario, cerrar } = useSesion();
  return (
    <main className="pantalla-ingreso">
      <div className="caja-ingreso">
        <h1>
          MADECI <span>ERP</span>
        </h1>
        <p>Hola, {usuario?.nombre}. ¿En qué empresa va a trabajar?</p>
        {empresas.length === 0 ? (
          <div className="panel-error sev-autorizacion" role="alert">
            Su usuario no tiene acceso a ninguna empresa activa. Pida a administración que le asigne
            un rol.
          </div>
        ) : (
          <ul className="lista-empresas" aria-label="Empresas">
            {empresas.map((e, i) => (
              <li key={e.id}>
                <button type="button" autoFocus={i === 0} onClick={() => elegirEmpresa(e.id)}>
                  <strong>{e.razon_social}</strong>
                  <span className="mono apagado">
                    {e.codigo} · {e.rut}-{e.dv}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
        <button type="button" className="enlace" onClick={() => void cerrar()}>
          Salir
        </button>
      </div>
    </main>
  );
}
